import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { BackButton } from '@/components';
import { toast } from '@/components';
import { AlertRuleView } from '@/features/AlertRuleView';
import { getAlertRuleCacheKey } from '@/constants/cache-keys';
import { AlertRuleViewSkeleton } from '@/features/AlertRuleView/components/AlertRuleViewSkeleton';
import { EventFeed } from '@/features/EventFeed';
import { AppRoutes } from '@/constants/routes';
import { EngineService } from '@/services/engine';
import { FullAlertRule } from '@/types';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useDocumentTitle, useResetQueryState } from '@/hooks';
import { getAlertRuleDisplayName } from '@/utils/alert-rules';
import { revalidateAlertUnitCapacity } from '@/features/SettingsView/hooks/useConfiguration';

export default function AlertRulePage() {
    useResetQueryState();
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = useState(false);

    const {
        data: alertRule,
        error,
        isLoading,
        mutate,
    } = useSWR<FullAlertRule>(id ? getAlertRuleCacheKey(id) : null, () =>
        EngineService.getAlertRule(id!),
    );

    useDocumentTitle('alertRule', getAlertRuleDisplayName(alertRule));

    useEffect(() => {
        if (error) {
            toast.error(t('toast.failedToLoad'));
        }
    }, [error, t]);

    if (!id) {
        return <Navigate to={AppRoutes.ALERT_RULES} replace />;
    }

    if (isLoading) {
        return <AlertRuleViewSkeleton />;
    }

    if (error || !alertRule) {
        return <Navigate to={AppRoutes.ALERT_RULES} replace />;
    }

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await EngineService.deleteAlertRule(id);
            toast.success(t('toast.deleteSuccess'));
            await revalidateAlertUnitCapacity();
            navigate(AppRoutes.ALERT_RULES, { replace: true });
        } catch {
            toast.error(t('toast.deleteError'));
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="h-full relative">
            <EventFeed context={{ type: 'alertRule', alertRuleId: id }} />
            <BackButton variant="floating" />

            <div className="h-full overflow-y-auto">
                <div className="flex-1 min-h-0 flex flex-col items-center">
                    <div className="w-full flex-1 min-h-0">
                        <AlertRuleView
                            alertRule={alertRule}
                            onUpdate={(updated) => void mutate(updated, false)}
                            onDelete={handleDelete}
                            isDeleting={isDeleting}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
